import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import AppConst from '../apis/appconst';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Select from 'react-select';

function ProcedureBillModal({ patientData, onClose, image, watermarkImage, footerImage }) {
    const [formData, setFormData] = useState({
        procedure_bill_id: '',
        ipd_bill_id: patientData?.ipd_bill_id || patientData?.opd_id || '',
        ipd_bill_uhid: patientData?.ipd_bill_uhid || patientData?.opd_uhid || patientData?.ot_uhid_no || '',
        ipd_no: patientData?.ipd_no || patientData?.opd_id || '',
        patient_name: patientData?.ipd_bill_patient_name || patientData?.opd_patient_name || '',
        patient_age: patientData?.ipd_bill_patient_age || patientData?.opd_guardien_age || patientData?.opd_patient_age || '',
        patient_gender: patientData?.ipd_bill_patient_gender || patientData?.opd_patient_gender || '',
        patient_phone: patientData?.ipd_bill_patient_phone || patientData?.opd_patient_mobile || '',
        patient_address: patientData?.ipd_bill_patient_address || patientData?.opd_address || '',
        bill_date: new Date().toISOString().slice(0, 16),
        created_at: '',
        total_amount: 0,
        discount_percent: 0,
        discount_amount: 0,
        final_amount: 0,
        payment_mode: 'Cash',
        remarks: '',
        items: [],
        services: [],
        payment_status: 'Pending',
        paid_amount: 0
    });

    const [searchKey, setSearchKey] = useState('');
    const [saving, setSaving] = useState(false);
    const [serviceOptions, setServiceOptions] = useState([]);
    const [isLoadingServices, setIsLoadingServices] = useState(false);
    const [headerImageUrl, setHeaderImageUrl] = useState(image || '');
    const [isLoadingBill, setIsLoadingBill] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentData, setPaymentData] = useState({
        amount: 0,
        payment_mode: 'Cash',
        received_by: '',
        remarks: ''
    });
    
    const modalBodyRef = useRef(null);
    const scrollPositionRef = useRef(0);
    const [hasPaymentTable, setHasPaymentTable] = useState(false);
    const [billFetched, setBillFetched] = useState(false);
    const [billList, setBillList] = useState([]);          // ✅ existing bills for this OPD
    const [loadingBillList, setLoadingBillList] = useState(false);



// ── Load one bill by id into the form (edit mode) ──
// ── Load ONE bill by its unique procedure_bill_id (edit mode) ──
const loadBillById = async (billId) => {
    if (!billId) return;
    setIsLoadingBill(true);
    try {
        const fd = new FormData();
        fd.append('procedure_bill_id', billId);   // ✅ unique key only

        const response = await axios.post(
            AppConst.BASE_URL + AppConst.getProcedureBillUrl,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setHasPaymentTable(!!response?.data?.has_payment_table);

        if (response?.data?.status && response.data.bill) {
            const bill = response.data.bill;
            const items = response.data.items || [];
            const payments = response.data.payments || [];
            setBillFetched(true);

            const mappedItems = items.map(item => ({
                service_id: item.service_id || item.cat_id || '',
                service_name: item.service_name || item.cat_name || '',
                qty: Number(item.qty || item.quantity || 1),
                unit_price: Number(item.unit_price || item.price || 0),
                discount: Number(item.discount || 0),
                final_price: Number(item.final_price || item.amount || 0)
            }));

            const total = mappedItems.reduce((s, it) => s + Number(it.final_price || 0), 0);
            const discountPercent = Number(bill.discount_percent || 0);
            const discountAmount = Number(bill.discount_amount || 0);
            const finalAmt = total - discountAmount;

            let paidAmount = Number(bill.paid_amount || 0);
            if (paidAmount === 0 && payments.length > 0) {
                paidAmount = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
            }
            const remainingBalance = Math.max(0, finalAmt - paidAmount);
            const originalFinal = Number(bill.original_final_amount || 0) || finalAmt;
            const paymentStatus = bill.payment_status ||
                (remainingBalance <= 0 ? 'Paid' : (paidAmount > 0 ? 'Partial' : 'Pending'));

                const loaded = {
                    procedure_bill_id: bill.procedure_bill_id || billId,
                    ipd_bill_id: bill.ipd_bill_id,
                    ipd_bill_uhid: bill.ipd_bill_uhid,
                    ipd_no: bill.ipd_no,
                    patient_name: bill.patient_name,
                    patient_age: bill.patient_age,
                    patient_gender: bill.patient_gender,
                    patient_phone: bill.patient_phone,
                    patient_address: bill.patient_address,
                    bill_date: bill.bill_date,
                    created_at: bill.created_at || bill.bill_date || '',
                    total_amount: total.toFixed(2),
                    discount_percent: discountPercent,
                    discount_amount: discountAmount.toFixed(2),
                    final_amount: remainingBalance.toFixed(2),
                    original_final_amount: originalFinal.toFixed(2),
                    payment_mode: bill.payment_mode || 'Cash',
                    remarks: bill.remarks || '',
                    items: mappedItems,
                    payment_status: paymentStatus,
                    paid_amount: paidAmount,
                    payments: payments
                };
    
                setFormData(prev => ({ ...prev, ...loaded }));
                toast.success('Procedure bill loaded');
                return loaded;   // ✅ hand the data straight back to the caller
            } else {
                toast.error('Bill not found');
                return null;
            }
        } catch (err) {
            console.error('loadBillById error:', err);
            toast.error('Failed to load bill');
            return null;
        } finally {
            setIsLoadingBill(false);
        }
    };


// ── Reset the form to a fresh blank bill (patient header stays from patientData) ──
const startNewBill = () => {
    setBillFetched(false);
    setFormData(prev => ({
        ...prev,
        procedure_bill_id: '',
        total_amount: '0.00',
        discount_percent: 0,
        discount_amount: '0.00',
        final_amount: '0.00',
        original_final_amount: '0.00',
        paid_amount: 0,
        payment_status: 'Pending',
        payment_mode: 'Cash',
        remarks: '',
        items: [],
        payments: [],
        created_at: ''
    }));
};

// ── Fetch this patient's previous bills (grouped by UHID) ──
const fetchBillListForPatient = async () => {
    const uhid = patientData?.ipd_bill_uhid || patientData?.opd_uhid || patientData?.ot_uhid_no || '';
    if (!uhid) { setBillList([]); return []; }

    setLoadingBillList(true);
    try {
        const fd = new FormData();
        fd.append('page_no', 'none');
        fd.append('uhid', uhid);   // ✅ groups all of this patient's bills

        const res = await axios.post(
            AppConst.BASE_URL + AppConst.getProcedureBillsListUrl,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        const rows = (res?.data?.status && Array.isArray(res.data.records)) ? res.data.records : [];
        setBillList(rows);
        return rows;
    } catch (err) {
        console.error('fetchBillListForPatient error:', err);
        setBillList([]);
        return [];
    } finally {
        setLoadingBillList(false);
    }
};

// ── Entry point on open: NO auto-load of an old bill ──
// ── Entry point on open: ALWAYS start blank; only show the list ──
const fetchExistingBill = async () => {
    // Never auto-load a bill. Form starts blank (new bill).
    startNewBill();
    // Show this patient's previous bills so the user can pick one to Edit/Print.
    await fetchBillListForPatient();
};
    // ✅ useEffect to fetch existing bill
    useEffect(() => {
        fetchExistingBill();
    }, [patientData]);

    // ✅ Fetch header image from config
    useEffect(() => {
        const fetchHeaderImage = async () => {
            try {
                if (image) {
                    console.log('Header image from props:', image);
                    setHeaderImageUrl(image);
                    return;
                }

                console.log('Fetching header image from config API...');
                const res = await axios.post(
                    `${AppConst.BASE_URL}${AppConst.getconfig}?config_type=lab`
                );

                if (res.data.status && res.data.header_logo) {
                    console.log('Header image from API:', res.data.header_logo);
                    setHeaderImageUrl(res.data.header_logo);
                } else {
                    console.warn('No header image in config response');
                }
            } catch (err) {
                console.error('Error fetching header image:', err);
            }
        };

        fetchHeaderImage();
    }, [image]);

    // ✅ Prevent mouse wheel from changing number input values
    useEffect(() => {
        const preventWheel = (e) => {
            if (document.activeElement.type === 'number') {
                e.preventDefault();
            }
        };

        document.addEventListener('wheel', preventWheel, { passive: false });

        return () => {
            document.removeEventListener('wheel', preventWheel);
        };
    }, []);

    const fetchServices = async (inputValue = '') => {
        setIsLoadingServices(true);
        try {
            const fd = new FormData();
            fd.append('cat_type', 'service');
            fd.append('page_no', 'none');
            fd.append('search_key', inputValue);
            fd.append('par_by', '1');

            const response = await axios.post(
                AppConst.BASE_URL + AppConst.getCatListUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response?.data?.status && Array.isArray(response.data.records)) {
                const services = response.data.records;
                
                const options = services.map(srv => ({
                    value: srv.cat_id || srv.id,
                    label: srv.cat_name || srv.name,
                    price: Number(srv.cat_price || srv.price || 0),
                    serviceData: srv
                }));

                setServiceOptions(options);
                setFormData(prev => ({ ...prev, services }));
            }
        } catch (err) {
            console.error('Error fetching services:', err);
        } finally {
            setIsLoadingServices(false);
        }
    };

    useEffect(() => {
        fetchServices();
    }, []);

    const handleServiceSelect = (selectedOption) => {
        if (!selectedOption) return;

        const service = selectedOption.serviceData;
        const price = Number(service.cat_price || service.price || 0);
        
        const newItem = {
            service_id: service.cat_id || service.id,
            service_name: service.cat_name || service.name,
            qty: 1,
            unit_price: price,
            discount: 0,
            final_price: price
        };

        setFormData(prev => {
            const updatedItems = [...prev.items, newItem];
            const totals = calculateTotals({ ...prev, items: updatedItems });
            return { ...prev, items: updatedItems, ...totals };
        });

        toast.success(`${newItem.service_name} added`);
    };

    const calculateTotals = (currentData) => {
        const items = currentData.items || [];
        const total = items.reduce((sum, item) => sum + Number(item.final_price || 0), 0);
        const discountPercent = Number(currentData.discount_percent || 0);
        const discountAmtDirect = Number(currentData.discount_amount || 0);
        
        let discountAmount = 0;
        let finalAmount = 0;
        
        // Calculate discount
        if (currentData._discountMode === 'amount') {
            discountAmount = discountAmtDirect > total ? total : discountAmtDirect;
        } else {
            discountAmount = (total * discountPercent) / 100;
        }
        
        // Calculate final amount (this is the amount after discount)
        finalAmount = total - discountAmount;
        
        // Get existing paid amount
        const paidAmount = Number(currentData.paid_amount || 0);
        
        // Calculate remaining balance
        const remainingBalance = Math.max(0, finalAmount - paidAmount);
        
        // Store original final amount (before any payments)
        // Only set if not already set or if items/discount changed
        let originalFinalAmount = Number(currentData.original_final_amount || 0);
        
        // If original_final_amount is 0 or not set, calculate it
        if (originalFinalAmount === 0 && finalAmount > 0) {
            originalFinalAmount = finalAmount;
        }
        
        // If total or discount changed, recalculate original
        if (currentData._recalculateOriginal) {
            originalFinalAmount = finalAmount;
            currentData._recalculateOriginal = false;
        }
        
        return {
            total_amount: total.toFixed(2),
            discount_amount: discountAmount.toFixed(2),
            final_amount: remainingBalance.toFixed(2), // This is the balance due
            original_final_amount: originalFinalAmount.toFixed(2),
            paid_amount: paidAmount.toFixed(2)
        };
    };

    const handleItemChange = (index, field, value) => {
        if (modalBodyRef.current) {
            scrollPositionRef.current = modalBodyRef.current.scrollTop;
        }
    
        setFormData(prev => {
            const updatedItems = [...prev.items];
            updatedItems[index] = { ...updatedItems[index], [field]: value };
    
            const item = updatedItems[index];
            const qty = Number(item.qty || 0);
            const unitPrice = Number(item.unit_price || 0);
            const discount = Number(item.discount || 0);
            item.final_price = (qty * unitPrice) - discount;
    
            // Recalculate totals
            const total = updatedItems.reduce((sum, it) => sum + Number(it.final_price || 0), 0);
            const discountPercent = Number(prev.discount_percent || 0);
            const discountAmt = (total * discountPercent) / 100;
            const finalAmt = total - discountAmt;
            const paidAmount = Number(prev.paid_amount || 0);
            const remainingBalance = Math.max(0, finalAmt - paidAmount);
            
            // Calculate original final amount
            let originalFinal = Number(prev.original_final_amount || 0);
            if (originalFinal === 0 || originalFinal === parseFloat(prev.final_amount) + paidAmount) {
                originalFinal = finalAmt;
            }
    
            setTimeout(() => {
                if (modalBodyRef.current) {
                    modalBodyRef.current.scrollTop = scrollPositionRef.current;
                }
            }, 0);
    
            return {
                ...prev,
                items: updatedItems,
                total_amount: total.toFixed(2),
                discount_amount: discountAmt.toFixed(2),
                final_amount: remainingBalance.toFixed(2), // Balance due
                original_final_amount: originalFinal.toFixed(2)
            };
        });
    };

    const removeItem = (index) => {
        setFormData(prev => {
            const updatedItems = prev.items.filter((_, i) => i !== index);
            const totals = calculateTotals({ ...prev, items: updatedItems });
            return { ...prev, items: updatedItems, ...totals };
        });
    };

    const handleDiscountChange = (value) => {
        const percent = Number(value || 0);
        setFormData(prev => {
            const items = prev.items || [];
            const total = items.reduce((sum, item) => sum + Number(item.final_price || 0), 0);
            const discountAmt = (total * percent) / 100;
            const finalAmt = total - discountAmt;
            const paidAmount = Number(prev.paid_amount || 0);
            const remainingBalance = Math.max(0, finalAmt - paidAmount);
            
            // Calculate original final amount (before payments)
            let originalFinal = Number(prev.original_final_amount || 0);
            if (originalFinal === 0 || originalFinal === parseFloat(prev.final_amount) + paidAmount) {
                originalFinal = finalAmt;
            }
            
            return {
                ...prev,
                discount_percent: value,
                discount_amount: discountAmt.toFixed(2),
                final_amount: remainingBalance.toFixed(2), // Balance due
                original_final_amount: originalFinal.toFixed(2),
                _discountMode: 'percent'
            };
        });
    };

    const handleDiscountAmountChange = (value) => {
        const discountAmt = Number(value || 0);
        setFormData(prev => {
            const items = prev.items || [];
            const total = items.reduce((sum, item) => sum + Number(item.final_price || 0), 0);
            const clampedDiscount = discountAmt > total ? total : discountAmt;
            const percent = total > 0 ? ((clampedDiscount / total) * 100) : 0;
            const finalAmt = total - clampedDiscount;
            const paidAmount = Number(prev.paid_amount || 0);
            const remainingBalance = Math.max(0, finalAmt - paidAmount);
            
            // Calculate original final amount (before payments)
            let originalFinal = Number(prev.original_final_amount || 0);
            if (originalFinal === 0 || originalFinal === parseFloat(prev.final_amount) + paidAmount) {
                originalFinal = finalAmt;
            }
            
            return {
                ...prev,
                discount_percent: percent.toFixed(2),
                discount_amount: value,
                final_amount: remainingBalance.toFixed(2), // Balance due
                original_final_amount: originalFinal.toFixed(2),
                _discountMode: 'amount'
            };
        });
    };

    // ✅ Handle Pay Bill - opens payment modal
   // ✅ Handle Pay Bill - opens payment modal
const handlePayBill = () => {
    if (formData.items.length === 0) {
        toast.error('No items to pay');
        return;
    }

    // Check if there's a due amount
    const dueAmount = parseFloat(formData.final_amount);
    if (dueAmount <= 0) {
        toast.error('Bill is already fully paid');
        return;
    }

    // Get logged user for received_by
    let receivedBy = '';
    let userName = '';
    let userDisplayName = '';
    
    try {
        const stored = sessionStorage.getItem("ldata");
        if (stored) {
            const u = JSON.parse(stored);
            // Try multiple possible field names for user name
            userName = u.user_name || u.username || u.fname || u.full_name || u.display_name || '';
            
            // Also try to get user_id for display
            const userId = u.user_id || u.id || '';
            
            // Create display name - prefer actual name, fallback to user_id
            if (userName) {
                userDisplayName = userName;
            } else if (userId) {
                userDisplayName = `User (ID: ${userId})`;
            } else {
                userDisplayName = 'Unknown User';
            }
            
            receivedBy = userDisplayName;
            
            console.log('✅ User data found:', { 
                userName, 
                userId, 
                userDisplayName,
                fullData: u 
            });
        } else {
            console.warn('⚠️ No ldata found in sessionStorage');
            // Try to get from user_data as fallback
            const userData = sessionStorage.getItem("user_data");
            if (userData) {
                const u = JSON.parse(userData);
                userName = u.name || u.user_name || u.username || '';
                if (userName) {
                    receivedBy = userName;
                }
            }
        }
    } catch (e) {
        console.warn("Failed to get user info:", e);
    }

    // If still empty, try to get from localStorage as last resort
    if (!receivedBy) {
        try {
            const stored = localStorage.getItem("user");
            if (stored) {
                const u = JSON.parse(stored);
                receivedBy = u.name || u.user_name || u.username || u.full_name || '';
            }
        } catch (e) {
            console.warn("Failed to get user from localStorage:", e);
        }
    }

    // If still empty, set default
    if (!receivedBy) {
        receivedBy = 'System User';
    }

    console.log('✅ Setting receivedBy to:', receivedBy);

    setPaymentData({
        amount: dueAmount,
        payment_mode: 'Cash',
        received_by: receivedBy,
        remarks: ''
    });
    setShowPaymentModal(true);
};

    // ✅ Process Payment
   // ✅ Process Payment
// ✅ Process Payment - Updated
const processPayment = async () => {
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
        toast.error('Please enter a valid amount');
        return;
    }

    const dueAmount = parseFloat(formData.final_amount);
    if (parseFloat(paymentData.amount) > dueAmount) {
        toast.error(`Amount cannot exceed due amount of ₹${dueAmount.toFixed(2)}`);
        return;
    }

    const { isConfirmed } = await Swal.fire({
        title: 'Confirm Payment',
        text: `Are you sure you want to process payment of ₹${parseFloat(paymentData.amount).toFixed(2)}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Pay',
        cancelButtonText: 'Cancel'
    });

    if (!isConfirmed) return;

    setSaving(true);
    Swal.fire({ title: 'Processing Payment...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
        const fd = new FormData();
        fd.append('procedure_bill_id', formData.procedure_bill_id);
        fd.append('amount', paymentData.amount);
        fd.append('payment_mode', paymentData.payment_mode);
        fd.append('remarks', paymentData.remarks || '');
        
        // Get logged user
        try {
            const stored = sessionStorage.getItem("ldata");
            if (stored) {
                const u = JSON.parse(stored);
                fd.append('received_by', u.user_id || '');
                fd.append('received_by_name', u.user_name || u.username || u.name || '');
                fd.append('opd_user', u.user_id || '');
            }
        } catch (e) {
            console.warn("attach user failed:", e);
        }

        const response = await axios.post(
            AppConst.BASE_URL + AppConst.payProcedureBillUrl,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        Swal.close();

        if (response?.data?.status) {
            toast.success('Payment processed successfully');
            setShowPaymentModal(false);
            
            // ✅ FIX: Update form data with proper values
            const newPaidAmount = parseFloat(formData.paid_amount || 0) + parseFloat(paymentData.amount);
            const newFinalAmount = parseFloat(formData.final_amount) - parseFloat(paymentData.amount);
            
            // Ensure we don't go below zero
            const finalAmount = Math.max(0, newFinalAmount);
            const newPaymentStatus = finalAmount <= 0 ? 'Paid' : 'Partial';

            setFormData(prev => ({
                ...prev,
                payment_status: newPaymentStatus,
                paid_amount: newPaidAmount,
                final_amount: finalAmount.toFixed(2) // ✅ Properly update to remaining amount
            }));
            
            // Refresh bill data to get latest from server
            await fetchExistingBill();
            
        } else {
            toast.error(response?.data?.message || 'Failed to process payment');
        }
    } catch (err) {
        Swal.close();
        console.error('Payment error:', err);
        toast.error('Failed to process payment');
    } finally {
        setSaving(false);
    }
};

    const handleSave = async () => {
        if (formData.items.length === 0) {
            toast.error('Please add at least one service');
            return;
        }

        const { isConfirmed } = await Swal.fire({
            title: 'Save Procedure Bill?',
            text: 'Do you want to save this procedure bill?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Save',
            cancelButtonText: 'Cancel'
        });

        if (!isConfirmed) return;

        setSaving(true);
        Swal.fire({ title: 'Saving...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

        try {
            const fd = new FormData();
            Object.keys(formData).forEach(key => {
                if (key !== 'items' && key !== 'services' && key !== 'created_at' && key !== 'payment_status' && key !== 'paid_amount') {
                    fd.append(key, formData[key] || '');
                }
            });
            fd.append('items', JSON.stringify(formData.items));

            // ✅ attach logged-in user from sessionStorage
            try {
                const stored = sessionStorage.getItem("ldata");
                if (stored) {
                    const u = JSON.parse(stored);
                    fd.append('logged_user_id', u.user_id || '');
                    fd.append('opd_user', u.user_id || '');
                }
            } catch (e) {
                console.warn("attach user failed:", e);
            }
            
            const response = await axios.post(
                AppConst.BASE_URL + AppConst.saveProcedureBillUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            Swal.close();

            if (response?.data?.status) {
                toast.success('Procedure bill saved successfully');
                const newBillId = response.data.procedure_bill_id || response.data.id;

                // ✅ Reload the just-saved bill exactly like Edit does,
                //    so hasPaymentTable / billFetched / final_amount are all set,
                //    which makes the Pay Due button appear.
                if (newBillId) {
                    await loadBillById(newBillId);
                }

                fetchBillListForPatient();  // ✅ new bill shows in the list
            } else {
                toast.error(response?.data?.message || 'Failed to save');
            }
        } catch (err) {
            Swal.close();
            console.error('Save error:', err);
            toast.error('Failed to save procedure bill');
        } finally {
            setSaving(false);
        }
    };

    const imageUrlToBase64 = async (url) => {
        if (!url) {
            console.error("imageUrlToBase64: Provided URL is empty or invalid.");
            return null;
        }

        console.log("🖼️ Attempting to fetch image from URL:", url);

        try {
            const response = await fetch(url, { cache: 'no-cache' });
            if (!response.ok) {
                console.error(`❌ Failed to fetch image. URL: ${url}, Status: ${response.status} ${response.statusText}`);
                return null;
            }
            
            const blob = await response.blob();
            console.log("📦 Blob type:", blob.type, "Size:", blob.size);
        
            if (!blob.type.startsWith('image/')) {
                console.error(`❌ The URL did not point to an image. URL: ${url}, MIME Type: ${blob.type}`);
                return null;
            }
        
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    console.log("✅ Image successfully converted to base64, length:", reader.result?.length);
                    resolve(reader.result);
                };
                reader.onerror = (error) => {
                    console.error("❌ FileReader error:", error);
                    reject(error);
                };
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error("❌ Error fetching or converting image. This might be a CORS issue.", { url, error });
            return null;
        }
    };

    const formatDateForDisplay = (dateStr) => {
        if (!dateStr) return new Date().toLocaleString();
        
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) {
                return new Date().toLocaleString();
            }
            
            const day = String(date.getDate()).padStart(2, '0');
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = months[date.getMonth()];
            const year = date.getFullYear();
            
            let hours = date.getHours();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            const minutes = String(date.getMinutes()).padStart(2, '0');
            
            return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
        } catch (error) {
            console.error('Date formatting error:', error);
            return new Date().toLocaleString();
        }
    };

    const generatePDF = async (billOverride = null) => {
        const src = billOverride || formData;
        if (!src.items || src.items.length === 0) {
            toast.error('No items to print');
            return;
        }
    
        const doc = new jsPDF('p', 'pt', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        let y = 0;
    
        const imageToUse = headerImageUrl || image;
    
        try {
            if (imageToUse) {
                console.log('🔄 Converting image to base64...');
                const headerImageBase64 = await imageUrlToBase64(imageToUse);
                
                if (headerImageBase64) {
                    console.log('✅ Base64 conversion successful!');
                    doc.addImage(headerImageBase64, 'PNG', 0, 0, pageWidth, 80);
                    y = 105;
                    console.log('✅ Header image added, content starts at y =', y);
                } else {
                    y = 40;
                }
            } else {
                y = 40;
            }
        } catch (error) {
            console.error('❌ Header image error:', error);
            y = 40;
        }
    
        // Title
               // Title — prefix comes from the page that opened this modal (OPD / Casualty / Emergency / Day Care)
               const billTitlePrefix = (patientData?.bill_source || patientData?.source || '').toString().trim();
               const billTitle = billTitlePrefix
                   ? `${billTitlePrefix.toUpperCase()} PROCEDURE BILL`
                   : 'PROCEDURE BILL';
       
               doc.setFontSize(16).setFont('helvetica', 'bold');
               doc.text(billTitle, pageWidth / 2, y, { align: 'center' });
               y += 30;
    
        // Patient Details
        const leftX = 30;
        const rightX = 330;
        const labelOffset = 110;
    
        const addInfoLine = (label1, value1, label2, value2) => {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold').text(label1, leftX, y);
            doc.setFont('helvetica', 'normal').text(`: ${value1}`, leftX + labelOffset, y);
    
            if (label2) {
                doc.setFont('helvetica', 'bold').text(label2, rightX, y);
                doc.setFont('helvetica', 'normal').text(`: ${value2}`, rightX + labelOffset, y);
            }
            y += 18;
        };
    
        const billDateDisplay = formatDateForDisplay(src.created_at || src.bill_date);
        
        addInfoLine('Bill No', `PRO-${src.procedure_bill_id || 'NEW'}`, 'Bill Date', billDateDisplay);
        addInfoLine('UHID', src.ipd_bill_uhid, 'OPD No', `000${src.ipd_no}`);
        addInfoLine('Patient Name', src.patient_name, 'Age/Gender', `${src.patient_age}/${src.patient_gender}`);
        addInfoLine('Phone', src.patient_phone, 'Address', src.patient_address);
    
        y += 10;
    
        // Items Table
        const tableData = src.items.map((item, index) => [
            index + 1,
            item.service_name,
            item.qty,
            Number(item.unit_price).toFixed(2),
            Number(item.discount).toFixed(2),
            Number(item.final_price).toFixed(2)
        ]);
    
        autoTable(doc, {
            startY: y,
            head: [['S.No', 'Procedure', 'Qty', 'Unit Price', 'Discount', 'Amount']],
            body: tableData,
            theme: 'grid',
            headStyles: { fillColor: [180, 220, 250], textColor: 0, fontStyle: 'bold' },
            styles: { fontSize: 10, cellPadding: 4 },
            columnStyles: {
                0: { halign: 'center', cellWidth: 40 },
                2: { halign: 'center', cellWidth: 40 },
                3: { halign: 'right', cellWidth: 80 },
                4: { halign: 'right', cellWidth: 70 },
                5: { halign: 'right', cellWidth: 80 }
            }
        });
    
        y = doc.lastAutoTable.finalY + 20;
    
        // Summary Section
        const rightAlign = pageWidth - 30;
        const labelX = pageWidth - 180;
    
        doc.setFontSize(10).setFont('helvetica', 'bold');
        doc.text('Total Amount:', labelX, y);
        doc.text(`${src.total_amount}`, rightAlign, y, { align: 'right' });
        y += 18;
    
        doc.text(`Discount (${src.discount_percent}%):`, labelX, y);
        doc.text(`${src.discount_amount}`, rightAlign, y, { align: 'right' });
        y += 18;
    
        // ✅ Show original final amount before payments
        const originalFinal = parseFloat(src.total_amount) - parseFloat(src.discount_amount);
        doc.text('Sub Total:', labelX, y);
        doc.text(`${originalFinal.toFixed(2)}`, rightAlign, y, { align: 'right' });
        y += 18;
    
        // ✅ Show paid amount
        const paidAmount = parseFloat(src.paid_amount || 0);
        if (paidAmount > 0) {
            doc.text('Paid Amount:', labelX, y);
            doc.text(`${paidAmount.toFixed(2)}`, rightAlign, y, { align: 'right' });
            y += 18;
        }
    
        // ✅ Show remaining/due amount
        doc.setFontSize(12).setFont('helvetica', 'bold');
        doc.text('Balance Due:', labelX, y);
        doc.text(`${src.final_amount}`, rightAlign, y, { align: 'right' });
        y += 25;
    
        // Payment Status
        doc.setFontSize(9).setFont('helvetica', 'normal');
        doc.text(`Payment Status: ${src.payment_status || 'Pending'}`, 30, y);
        y += 15;
    
        // Payment Mode
        doc.text(`Payment Mode: ${src.payment_mode}`, 30, y);
        y += 15;
    
        if (formData.remarks) {
            doc.text(`Remarks: ${src.remarks}`, 30, y);
            y += 15;
        }
    
        // ============================================
               // ============================================
        // ✅ PAYMENT TRANSACTIONS TABLE
        // ============================================
        try {
            // Fetch payment transactions from API
            const fd = new FormData();
            fd.append('procedure_bill_id', src.procedure_bill_id);

            const response = await axios.post(
                AppConst.BASE_URL + AppConst.getProcedureBillPaymentsUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response?.data?.status && response.data.payments && response.data.payments.length > 0) {
                const payments = response.data.payments;

                // Add heading for payment transactions
                y += 10;
                doc.setFontSize(11).setFont('helvetica', 'bold');
                doc.text('Payment Transactions', 30, y);
                y += 20;

                // Create payment table data
                const paymentTableData = payments.map((payment, index) => [
                    index + 1,
                    `${Number(payment.amount).toFixed(2)}`,
                    payment.payment_mode || 'Cash',
                    payment.received_by_name || 'Unknown',
                    payment.payment_date ? formatDateForDisplay(payment.payment_date) : ''
                ]);

                autoTable(doc, {
                    startY: y,
                    head: [['S.No', 'Amount', 'Mode of Payment', 'Received By', 'Date']],
                    body: paymentTableData,
                    theme: 'grid',
                    headStyles: { fillColor: [180, 220, 250], textColor: 0, fontStyle: 'bold' },
                    styles: { fontSize: 10, cellPadding: 4 },
                    columnStyles: {
                        0: { halign: 'center', cellWidth: 40 },
                        1: { halign: 'right', cellWidth: 80 },
                        2: { halign: 'center', cellWidth: 90 },
                        3: { halign: 'left' },              // flexes to fill width
                        4: { halign: 'left', cellWidth: 90 }
                    }
                });

                y = doc.lastAutoTable.finalY + 20;
            } else {
                // No payments found, show message
                y += 10;
                doc.setFontSize(9).setFont('helvetica', 'italic');
                doc.text('No payment transactions recorded', 30, y);
                y += 20;
            }
        } catch (error) {
            console.error('Error fetching payment transactions for PDF:', error);
            // Continue with PDF generation even if payments fail
            y += 10;
            doc.setFontSize(9).setFont('helvetica', 'italic');
            doc.text('Payment transactions unavailable', 30, y);
            y += 20;
        }
        // Footer
        doc.setFontSize(9).setFont('helvetica', 'normal');
        doc.text('Authorized Signature', pageWidth - 30, pageHeight - 30, { align: 'right' });
    
        console.log('✅ PDF GENERATION COMPLETE');
    
        const pdfBlob = doc.output('blob');
        window.open(URL.createObjectURL(pdfBlob), '_blank');
    };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '42px',
            borderColor: '#ced4da',
            '&:hover': {
                borderColor: '#86b7fe'
            }
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected 
                ? '#0d6efd' 
                : state.isFocused 
                ? '#e7f1ff' 
                : 'white',
            color: state.isSelected ? 'white' : '#212529',
            cursor: 'pointer',
            padding: '10px 12px'
        })
    };

    const formatOptionLabel = ({ label, price }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{label}</span>
            <span className="badge bg-success ms-2">₹{price.toFixed(2)}</span>
        </div>
    );

    // ✅ Payment Modal Component

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                <div className="modal-header">
                        <h5 className="modal-title">
                            {formData.procedure_bill_id ? 'Edit Procedure Bill' : 'New Procedure Bill'}
                            {formData.procedure_bill_id && formData.payment_status && (
                                <span className={`ms-2 badge ${formData.payment_status === 'Paid' ? 'bg-success' : formData.payment_status === 'Partial' ? 'bg-warning' : 'bg-secondary'}`}>
                                    {formData.payment_status}
                                </span>
                            )}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>

                    <div className="modal-body" ref={modalBodyRef}>
                        {isLoadingBill ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2">Loading procedure bill...</p>
                            </div>
                        ) : (
                            <>

                            {/* ✅ Existing bills for this OPD */}
                            {/* ✅ Previous procedure bills for this patient (grouped by UHID) */}
                            {(billList.length > 0 || loadingBillList) && (
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <h6 className="card-title mb-0">
                                                    <i className="ri-file-list-3-line me-1"></i>
                                                    Previous Procedure Bills ({billList.length})
                                                </h6>
                                                <button type="button" className="btn btn-sm btn-outline-primary"
                                                    onClick={startNewBill}>
                                                    <i className="ri-add-line me-1"></i>New Bill
                                                </button>
                                            </div>

                                            {loadingBillList ? (
                                                <div className="text-center py-2">
                                                    <span className="spinner-border spinner-border-sm text-primary me-2"></span>
                                                    Loading bills...
                                                </div>
                                            ) : (
                                                <div className="table-responsive">
                                                    <table className="table table-sm table-bordered mb-0">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th style={{width:'50px'}}>#</th>
                                                                <th>Bill No</th>
                                                                <th>Patient</th>
                                                                <th>UHID</th>
                                                                <th>Age/Gender</th>
                                                                <th>Date &amp; Time</th>
                                                                <th className="text-end">Amount</th>
                                                                <th>Status</th>
                                                                <th style={{width:'120px'}}>Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {billList.map((b, i) => {
                                                                const isOpen = String(b.procedure_bill_id) === String(formData.procedure_bill_id);
                                                                const amt = Number(
                                                                    b.original_final_amount ??
                                                                    b.final_amount ??
                                                                    b.total_amount ?? 0
                                                                );
                                                                const st = b.payment_status || 'Pending';
                                                                return (
                                                                    <tr key={b.procedure_bill_id || i}
                                                                        style={isOpen ? {background:'#eef4ff'} : {}}>
                                                                        <td>{i + 1}</td>
                                                                        <td><strong>PRO-{b.procedure_bill_id}</strong></td>
                                                                        <td>{b.patient_name || '-'}</td>
                                                                        <td>{b.ipd_bill_uhid || '-'}</td>
                                                                        <td>{b.patient_age || '-'} / {b.patient_gender || '-'}</td>
                                                                        <td>{formatDateForDisplay(b.created_at || b.bill_date)}</td>
                                                                        <td className="text-end">₹{amt.toFixed(2)}</td>
                                                                        <td>
                                                                            <span className={`badge ${
                                                                                st === 'Paid' ? 'bg-success' :
                                                                                st === 'Partial' ? 'bg-warning text-dark' :
                                                                                'bg-secondary'
                                                                            }`}>{st}</span>
                                                                        </td>
                                                                        <td>
                                                                            <button type="button"
                                                                                className="btn btn-sm btn-outline-warning me-1"
                                                                                title="Edit this bill"
                                                                                onClick={() => loadBillById(b.procedure_bill_id)}>
                                                                                <i className="ri-pencil-line"></i>
                                                                            </button>
                                                                            <button type="button"
                                                                                className="btn btn-sm btn-outline-dark"
                                                                                title="Print this bill"
                                                                                onClick={async () => {
                                                                                    const loaded = await loadBillById(b.procedure_bill_id);
                                                                                    if (loaded) generatePDF(loaded);   // ✅ print exact data, no timing race
                                                                                }}>
                                                                                <i className="ri-printer-line"></i>
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <h6 className="card-title">Patient Details</h6>
                                        <div className="row">
                                            <div className="col-md-3">
                                                <strong>UHID:</strong> {formData.ipd_bill_uhid}
                                            </div>
                                            <div className="col-md-3">
                                                <strong>Name:</strong> {formData.patient_name}
                                            </div>
                                            <div className="col-md-3">
                                                <strong>Age/Gender:</strong> {formData.patient_age}/{formData.patient_gender}
                                            </div>
                                            <div className="col-md-3">
                                                <strong>OPD No:</strong> 000{formData.ipd_no}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="card mb-3">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <div className="mb-3">
                                                    <label className="form-label">Search & Select Procedure/Service</label>
                                                    <Select
                                                        options={serviceOptions}
                                                        onChange={handleServiceSelect}
                                                        onInputChange={(inputValue) => {
                                                            if (inputValue.length > 0) {
                                                                fetchServices(inputValue);
                                                            }
                                                        }}
                                                        formatOptionLabel={formatOptionLabel}
                                                        styles={customSelectStyles}
                                                        placeholder="Type to search services..."
                                                        isClearable
                                                        isSearchable
                                                        isLoading={isLoadingServices}
                                                        noOptionsMessage={() => "No services found"}
                                                        loadingMessage={() => "Loading services..."}
                                                        value={null}
                                                        menuPosition="fixed"
                                                        menuPortalTarget={document.body}
                                                    />
                                                    <small className="text-muted">
                                                        Start typing to search, click to add service to the bill
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="card mb-3">
                                    <div className="card-body">
                                        <h6 className="card-title">Procedures/Services</h6>
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th>S.No</th>
                                                        <th>Service</th>
                                                        <th style={{ width: '80px' }}>Qty</th>
                                                        <th style={{ width: '120px' }}>Unit Price</th>
                                                        <th style={{ width: '120px' }}>Discount</th>
                                                        <th style={{ width: '120px' }}>Final</th>
                                                        <th style={{ width: '80px' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {formData.items.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="7" className="text-center text-muted">
                                                                No services added. Search and select services above.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        formData.items.map((item, index) => (
                                                            <tr key={index}>
                                                                <td>{index + 1}</td>
                                                                <td>{item.service_name}</td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control form-control-sm"
                                                                        value={item.qty}
                                                                        onChange={(e) => handleItemChange(index, 'qty', e.target.value)}
                                                                        onWheel={(e) => e.target.blur()}
                                                                        min="1"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control form-control-sm"
                                                                        value={item.unit_price}
                                                                        onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                                                                        onWheel={(e) => e.target.blur()}
                                                                        min="0"
                                                                        step="0.01"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control form-control-sm"
                                                                        value={item.discount}
                                                                        onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                                                                        onWheel={(e) => e.target.blur()}
                                                                        min="0"
                                                                        step="0.01"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control form-control-sm"
                                                                        value={Number(item.final_price).toFixed(2)}
                                                                        readOnly
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <button
                                                                        className="btn btn-sm btn-danger"
                                                                        onClick={() => removeItem(index)}
                                                                    >
                                                                        <i className="ri-delete-bin-line"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <div className="card">
                                    <div className="card-body">
                                        <h6 className="card-title">Payment Details</h6>
                                        
                                        <div className="row">
    <div className="col-md-3">
        <div className="mb-3">
            <label className="form-label">Total Amount</label>
            <input
                type="text"
                className="form-control"
                value={formData.total_amount}
                readOnly
                style={{ background: '#EEE' }}
            />
        </div>
    </div>
    <div className="col-md-3">
        <div className="mb-3">
            <label className="form-label">Discount (%)</label>
            <input
                type="number"
                className="form-control"
                value={formData.discount_percent}
                onChange={(e) => handleDiscountChange(e.target.value)}
                onWheel={(e) => e.target.blur()}
                min="0"
                max="100"
                step="0.01"
            />
        </div>
    </div>
    <div className="col-md-3">
        <div className="mb-3">
            <label className="form-label">Discount Amount (₹)</label>
            <input
                type="number"
                className="form-control"
                value={formData.discount_amount}
                onChange={(e) => handleDiscountAmountChange(e.target.value)}
                onWheel={(e) => e.target.blur()}
                min="0"
                step="0.01"
            />
        </div>
    </div>
    <div className="col-md-3">
        <div className="mb-3">
            <label className="form-label"><strong>Final Amount</strong></label>
            <input
                type="text"
                className="form-control fw-bold"
                value={formData.original_final_amount || formData.total_amount}
                readOnly
                style={{ background: '#EEE', color: '#0d6efd' }}
            />
        </div>
    </div>
    {hasPaymentTable && (
    <>
        <div className="col-md-3">
            <div className="mb-3">
                <label className="form-label"><strong>Amount Paid</strong></label>
                <input type="text" className="form-control fw-bold"
                    value={formData.paid_amount || 0}
                    readOnly style={{ background: '#EEE', color: '#28a745' }} />
            </div>
        </div>
        <div className="col-md-3">
            <div className="mb-3">
                <label className="form-label"><strong>Balance Due</strong></label>
                <input type="text" className="form-control fw-bold"
                    value={formData.final_amount} readOnly
                    style={{ background: '#EEE', color: formData.final_amount > 0 ? '#dc3545' : '#28a745' }} />
            </div>
        </div>
        </>

)}


{! hasPaymentTable && (
    <>

                                            <div className="col-md-4">
                                                <div className="mb-3">
                                                    <label className="form-label">Payment Mode</label>
                                                    <select
                                                        className="form-control"
                                                        value={formData.payment_mode}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, payment_mode: e.target.value }))}
                                                    >
                                                        <option value="Cash">Cash</option>
                                                        <option value="Online">Online</option>
                                                        <option value="Card">Card</option>
                                                    </select>
                                                </div> 
                                            </div>
                                            </>

)}  

                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Remarks</label>
                                                    <textarea
                                                        className="form-control"
                                                        rows="2"
                                                        value={formData.remarks}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                                                        placeholder="Enter any remarks..."
                                                    ></textarea>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            <i className="ri-close-line me-1"></i>
                            Close
                        </button>
                      
    {hasPaymentTable && billFetched && parseFloat(formData.final_amount) > 0 && (
        <button type="button" className="btn btn-success"
            onClick={handlePayBill}
            disabled={saving || formData.items.length === 0 || isLoadingBill}>
            <i className="ri-money-rupee-circle-line me-1"></i>
            Pay Due
        </button>
    )}

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={saving || formData.items.length === 0 || isLoadingBill}
                        >
                            {saving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    Saving...
                                </>
                            ) : (
                                formData.procedure_bill_id ? 'Update' : 'Save'
                            )}
                        </button>
                        <button
                            type="button"
                            className="btn btn-dark"
                            onClick={generatePDF}
                            disabled={formData.items.length === 0 || isLoadingBill}
                        >
                            <i className="ri-printer-line me-1"></i>
                            Print Bill
                        </button>
                    </div>
                </div>
            </div>
            {showPaymentModal && (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
        <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title">
                        <i className="ri-money-rupee-circle-line me-2"></i>
                        Process Payment
                    </h5>
                    <button type="button" className="btn-close" onClick={() => setShowPaymentModal(false)}></button>
                </div>
                <div className="modal-body">
                    <div className="mb-3">
                        <label className="form-label fw-bold">Bill Number</label>
                        <input type="text" className="form-control" value={`PRO-${formData.procedure_bill_id || 'NEW'}`} readOnly style={{ background: '#F8F9FA' }} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-bold">Patient Name</label>
                        <input type="text" className="form-control" value={formData.patient_name} readOnly style={{ background: '#F8F9FA' }} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-bold">Total Due Amount</label>
                        <input type="text" className="form-control fw-bold" value={`₹${parseFloat(formData.final_amount).toFixed(2)}`} readOnly style={{ background: '#EEE', color: '#0d6efd' }} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-bold">
                            Payment Amount <span className="text-danger">*</span>
                        </label>
                        <input
                            type="number"
                            className="form-control"
                            value={paymentData.amount}
                            onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                            onWheel={(e) => e.target.blur()}
                            min="0"
                            max={parseFloat(formData.final_amount)}
                            step="0.01"
                            required
                        />
                        <small className="text-muted">Maximum: ₹{parseFloat(formData.final_amount).toFixed(2)}</small>
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-bold">
                            Payment Mode <span className="text-danger">*</span>
                        </label>
                        <select
                            className="form-control"
                            value={paymentData.payment_mode}
                            onChange={(e) => setPaymentData(prev => ({ ...prev, payment_mode: e.target.value }))}
                        >
                            <option value="Cash">Cash</option>
                            <option value="Online">Online</option>
                            <option value="Card">Card</option>
                            <option value="UPI">UPI</option>
                        </select>
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-bold">Received By</label>
                        <div className="input-group">
                            <span className="input-group-text">
                                <i className="ri-user-line"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                value={paymentData.received_by}
                                readOnly
                                style={{ background: '#F8F9FA' }}
                            />
                        </div>
                        <small className="text-muted">Auto-filled from logged user</small>
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-bold">Remarks</label>
                        <textarea
                            className="form-control"
                            rows="2"
                            value={paymentData.remarks}
                            onChange={(e) => setPaymentData(prev => ({ ...prev, remarks: e.target.value }))}
                            placeholder="Payment remarks..."
                        ></textarea>
                    </div>
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentModal(false)}>
                        <i className="ri-close-line me-1"></i>
                        Close
                    </button>
                    <button
                        type="button"
                        className="btn btn-success"
                        onClick={processPayment}
                        disabled={saving || !paymentData.amount || parseFloat(paymentData.amount) <= 0}
                    >
                        {saving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Processing...
                            </>
                        ) : (
                            <>
                                <i className="ri-check-line me-1"></i>
                                Pay Now
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    </div>
)}
        </div>
    );
}

export default ProcedureBillModal;